/**
	* Attribution box view
	* Handles: map name, attributions
	* @module Attributions
*/

/// Imports

// React
import { useState, useEffect } from "react";

// jQuery
import $ from "jquery";

// OpenLayers
import OlMap from "ol/Map";
import Layer from "ol/layer/Layer";
import Source, { Attribution } from 'ol/source/Source';

// UI
import TC from "views/popups/TC";

// Popups
import PermalinkPopup from "views/popups/Permalink";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// Lib
import MapToolMode from "lib/enums/maptoolmode";
import { getVisibleBaseLayers } from "lib/utilities/map";
import { assetURL } from "lib/utilities/asset";
import { getToolEnabled } from "lib/utilities/toolmodes";

interface AttributionsProps {
	olMap:OlMap
}

export default function Attributions({olMap}:AttributionsProps) {
	const [activeBaseLayer, setActiveBaseLayer] = useState<Layer>();
	const [baseAttrShowing, setBaseAttrShowing] = useState<boolean>(_dimensions.width > 500);
	const [toolsMaster, setToolsMaster] = useState<MapToolMode[]>(null); // Only for the master url tool override
		
	const mapConfig:any = olMap.get("opus-config");
	const minAttrDisplayWidth:number = _config.minAttrDisplayWidth || 400;
	const minAttrDisplayHeight:number = _config.minAttrDisplayHeight || 200;
	
	// Initialize
	useEffect(() => {
		initialize();
		
		$("#base-layer-attr").toggle(baseAttrShowing);
		
		_events.on("set-tools-master", () => {
			setToolsMaster(_config.toolsMaster != null ? [..._config.toolsMaster] : null);
		});
		
		_events.invoke("set-tools-master");
	}, []);
	
	// Window dimensions changed
	useEffect(() => {
		scaleRect();
	}, [_dimensions]);
	
	/*
		Initialize
	*/
	const initialize = () => {
		_events.on("active-base-layer", (layer:Layer) => {
			setActiveBaseLayer(layer);
		});
	}
	
	/**
		* Should TC shrink?
		* @return true/false depending on screen size
	*/
	const shouldShowSmallTC = ():boolean => {
		return _dimensions.width < minAttrDisplayWidth || _dimensions.height < minAttrDisplayHeight;
	}
	
	/**
		* Draw terms and conditions
		* @return The terms and conditions if the screen is big enough
	*/
	const drawTC = () => {
		return (
			<div className="attributions__tc" onClick={() => _ui.showPopup(TC)}>
				{!shouldShowSmallTC() ? getLocaleString("tclabel") : getLocaleString("tclabelsmall")}
			</div>
		)
	}
	
	/**
		Dynamically scale the attribution box depending on screen size
	*/
	const scaleRect = () => {
		const wideEnough:boolean = _dimensions.width > 500;
		const tallEnough:boolean = _dimensions.height > 500;
		const bigEnough:boolean = wideEnough && tallEnough;
		
		$("#attributions").css("max-width", wideEnough ? "" : 160);
		$("#attributions-map-name").css("font-size", bigEnough ? "" : "10px");
		$("#attributions").css("font-size", bigEnough ? "" : "8px");
	}
	
	/*
		Get the attribution of the active layer
	*/
	const getLayerAttribution = () => {
		if(!activeBaseLayer) return "";
		
		const source:Source = activeBaseLayer.getSource();
		const attrib:Attribution = source.getAttributions();
		
		return attrib?.(null) as string || "";
	}
	
	/*
		Toggle the base layer attribution layer
	*/
	const toggleBaseLayerAttr = () => {
		$("#base-layer-attr").toggle();

		setBaseAttrShowing(!baseAttrShowing);
		
		_events.invoke("base-attribution-showing", !baseAttrShowing);
	}
	
	/*
		Draw the permalink button
	*/
	const drawPermalinkButton = () => {
		return (
			<div 
				className="attributions__permalink-button"
				onClick={() => _ui.showPopup(PermalinkPopup)}
			>
				<img src={assetURL("/assets/images/permalink.png")} />
			</div>
		)
	}
	
	if(!_config.showOpusAttribution) return;
	
	const layerAttribution:any = getLayerAttribution();
	
	// For quick reference in PDF generator
	olMap.set("active-layer-attribution", layerAttribution);
	PermalinkPopup.set("opus-config", mapConfig);
	return (
		<>
			<div id="attributions" className="attributions">
				{getToolEnabled(MapToolMode.Permalink, mapConfig) && drawPermalinkButton()}
				
				<div id="attributions-map-name" className={`attributions__map-name${getToolEnabled(MapToolMode.Permalink, mapConfig) ? ' attributions__map-name--with-permalink' : ''}`}>
					{mapConfig.name}
				</div>
				
				<div id="base-layer-attr">
					<div>
						{mapConfig.attribution}
					</div>
					<div className="attributions__base-layer">
						{layerAttribution?.[0] ?? ""}
					</div>
				</div>
				
				<div onClick={() => toggleBaseLayerAttr()} className="attributions__base-layer attributions__base-layer--btn">
					{baseAttrShowing ? getLocaleString("hidelayerattr") : getLocaleString("showlayerattr")}
				</div>
				
				{drawTC()} 
			</div>
			<div className="attributions__opus-version">
				v{process.env.APP_VERSION}
			</div>
		</>
	);
}